import React, { useMemo, useEffect } from "react";
import { graphql, PageProps } from "gatsby";
import { RequestAQuotePageQuery } from "types/graphql-types";
import { CruiseSuiteFormProps } from "components/DefaultSubscribeForm/DefaultSubscribeForm";
import { voyageToDataLayer } from "src/utils/analytics/tracking";

import CuiseCardSummary from "../../components/CruiseCardSummary/CuiseCardSummary";
import {
  getCardSummaryContent,
  getSuite,
  priceToNumber,
  useCruiseCard,
  useCruiseCardPrice
} from "./utils";
import RequestAQuoteTemplate from "./RequestAQuoteTemplate";
import { useLanguage } from "src/utils/localization/languages/I18nContext";

function parseParams(
  path: string
): { cruiseCode?: string; suiteSlug?: string } {
  if (!path)
    return {
      cruiseCode: undefined,
      suiteSlug: undefined
    };

  const params = path.replace(".html", "").split("/", 2);
  return {
    cruiseCode: params[0],
    suiteSlug: params[1]
  };
}

const RequestAQuotePageWithCard: React.FC<PageProps<RequestAQuotePageQuery>> = (
  props
) => {
  const { location } = props;
  const language = useLanguage();

  const { cruiseCode, suiteSlug } = parseParams(props.params["*"]);
  const { data: remoteCruiseContent } = useCruiseCard(cruiseCode);
  const { cruise, specialVoyage } = remoteCruiseContent?.result || {
    cruise: undefined,
    specialVoyage: undefined
  };
  const cruiseOrSpecialVoyage = cruise || specialVoyage;
  const ship = cruiseOrSpecialVoyage?.data?.ship;
  const suite =
    ship && suiteSlug ? getSuite(ship._id, suiteSlug, ship.suites) : undefined;

  const { price, offers } = useCruiseCardPrice(
    cruiseCode,
    suite,
    location,
    language
  );
  const { title: cruiseTitle } = getCardSummaryContent(cruise, specialVoyage);

  const cruiseSuiteFormProps: CruiseSuiteFormProps = useMemo(
    () => ({
      voyageName: cruiseTitle,
      voyageCode: cruiseCode,
      departureDate: cruiseOrSpecialVoyage?.data?.departureDate,
      voyageLength: `${cruiseOrSpecialVoyage?.data?.days}`,
      shipName: ship?.name,
      price: price ? priceToNumber(price) : undefined,
      suiteVariation: suite?.category?.name?.localized
    }),
    [
      cruiseCode,
      cruiseOrSpecialVoyage?.data?.days,
      cruiseOrSpecialVoyage?.data?.departureDate,
      cruiseTitle,
      price,
      ship?.name,
      suite?.category?.name?.localized
    ]
  );

  useEffect(() => {
    if (cruiseOrSpecialVoyage && price) {
      voyageToDataLayer({
        name: cruiseSuiteFormProps.voyageName,
        code: cruiseSuiteFormProps.voyageCode,
        departure_date: cruiseSuiteFormProps.departureDate,
        days_duration: cruiseSuiteFormProps.voyageLength
          ? parseInt(cruiseSuiteFormProps.voyageLength)
          : undefined,
        ship_name: cruiseSuiteFormProps.shipName,
        price: cruiseSuiteFormProps.price,
        sale_state: "price",
        suite: cruiseSuiteFormProps.suiteVariation
      });
    }
  }, [cruiseOrSpecialVoyage, price, cruiseSuiteFormProps]);

  const card = useMemo(() => {
    return (
      <div key="card">
        {cruiseOrSpecialVoyage && price && (
          <CuiseCardSummary
            key="cruise-card-summary"
            cruise={cruise}
            specialVoyage={specialVoyage}
            suite={suite}
            price={price}
            offers={offers}
          />
        )}
      </div>
    );
  }, [cruise, cruiseOrSpecialVoyage, offers, price, specialVoyage, suite]);

  return (
    <RequestAQuoteTemplate
      {...props}
      card={card}
      cruiseSuiteForm={cruiseSuiteFormProps}
    />
  );
};

export const query = graphql`
  query RequestAQuotePageWithCard($languageCode: String) {
    page: sanityRequestAQuote(_id: { eq: "request-a-quote-page" }) {
      metadata {
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      heading {
        ...PageBuilderOpeningText
      }
    }
    thankYouPageURL: sitePage(
      context: {
        sanityId: { regex: "/request-a-quote-thankyou-page$/" }
        languageCode: { eq: $languageCode }
      }
    ) {
      path
    }
  }
`;

export default RequestAQuotePageWithCard;
